/**
 * Inline quick add.
 *
 * The size picker opens over the card image rather than in a dialog. Choosing a
 * size selects it, and a separate Add to Cart button commits, matching the two
 * step flow in live's quick add drawer. On success the button swaps to Added
 * with a tick before resetting.
 */

const ADDED_DURATION = 2500;

/**
 * The gallery arrows sit beneath the panel and steal the interaction, so the
 * card is marked while a panel is open and the arrows are switched off against
 * that mark. Done here as well as in CSS so it does not rest on :has() alone.
 */
function markCard(panel, isOpen) {
  // Mark both the card and the quick add container. The container owns the
  // stacking context the panel lives in, so it is the one that has to rise.
  const targets = [
    panel.closest('product-card') || panel.closest('.product-grid__card'),
    panel.closest('.quick-add'),
    panel.closest('quick-add-component'),
  ].filter(Boolean);

  targets.forEach((node) => {
    if (isOpen) {
      node.setAttribute('quick-add-open', '');
    } else {
      node.removeAttribute('quick-add-open');
    }
  });
}

function setPanel(panel, isOpen) {
  panel.toggleAttribute('open-panel', isOpen);
  markCard(panel, isOpen);
}

function closeAll(except) {
  document.querySelectorAll('[data-quick-add-panel][open-panel]').forEach((panel) => {
    if (panel !== except) setPanel(panel, false);
  });
}

function selectOption(option) {
  const panel = option.closest('[data-quick-add-panel]');
  if (!panel) return;

  panel.querySelectorAll('[data-quick-add-variant]').forEach((node) => {
    node.setAttribute('aria-checked', node === option ? 'true' : 'false');
  });

  const submit = panel.querySelector('[data-quick-add-submit]');
  if (submit) {
    submit.disabled = false;
    submit.dataset.variantId = option.dataset.quickAddVariant;
  }
}

async function addToCart(submit) {
  const component = submit.closest('quick-add-component');
  const form = component?.querySelector('form');
  const variantId = submit.dataset.variantId;
  if (!form || !variantId) return;

  const variantInput = form.querySelector('input[name="id"]');
  if (variantInput) {
    variantInput.value = variantId;
    variantInput.removeAttribute('disabled');
  }

  submit.disabled = true;

  try {
    // The theme refreshes the cart from section HTML returned with the add, so
    // the same sections are requested here. Posting without them added the item
    // but left the cart icon and drawer stale until a reload.
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((node) => node.dataset.sectionId)
      .filter(Boolean);

    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity: 1 }],
        sections: sectionIds.join(','),
      }),
    });

    if (!response.ok) throw new Error(`Add to cart failed with ${response.status}`);

    const result = await response.json();
    submit.dataset.added = 'true';

    // cart:update is the event the theme listens on. The name I used before did
    // not exist, so nothing was ever notified.
    const cart = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`)
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null);

    const event = new Event('cart:update', { bubbles: true });
    event.detail = {
      resource: cart ?? undefined,
      sourceId: 'quick-add-inline',
      data: {
        source: 'quick-add-inline',
        itemCount: 1,
        variantId: String(variantId),
        sections: result.sections,
      },
    };
    (component || document).dispatchEvent(event);

    setTimeout(() => {
      delete submit.dataset.added;
      submit.disabled = false;
      const panel = component.querySelector('[data-quick-add-panel]');
      if (panel) setPanel(panel, false);
    }, ADDED_DURATION);
  } catch (error) {
    submit.disabled = false;
    // Fall back to a normal form submit so the customer is never stuck.
    form.requestSubmit();
  }
}

document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-quick-add-inline]');

  if (trigger) {
    event.preventDefault();
    const component = trigger.closest('quick-add-component');
    const panel = component?.querySelector('[data-quick-add-panel]');
    if (!panel) return;

    const isOpen = panel.hasAttribute('open-panel');
    closeAll(panel);
    setPanel(panel, !isOpen);
    return;
  }

  const option = event.target.closest('[data-quick-add-variant]');
  if (option) {
    event.preventDefault();
    if (option.getAttribute('aria-disabled') === 'true') return;
    selectOption(option);
    return;
  }

  const submit = event.target.closest('[data-quick-add-submit]');
  if (submit) {
    event.preventDefault();
    addToCart(submit);
    return;
  }

  if (!event.target.closest('[data-quick-add-panel]')) closeAll(null);
});

document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape') closeAll(null);
});
